'use client'

import { popAllModals, pushModal } from '@/components/modals'
import { MIXPANEL_CLIENT_EVENT_NAMES } from '@/lib/mixpanel/constant'
import sendToBothAnalytics from '@/lib/mixpanel/sendToBothAnalytics'
import type { User } from '@/lib/types'
import { ExternalLink, Loader2Icon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/navigation'
import { useState } from 'react'

type TTemplateActionsProps = {
  templateId: string
  templateTitle: string
  previewUrl: string
  user: User | null
}

/**
 * Template action buttons (View in new tab, Use template)
 * @param templateId - Template ID for routing and analytics
 * @param templateTitle - Template title for analytics
 * @param previewUrl - URL to open in new tab
 * @param user - Current user for auth checks
 * @returns Template action buttons component
 */
export function TemplateActions({
  templateId,
  templateTitle,
  previewUrl,
  user,
}: TTemplateActionsProps) {
  const [requesting, setRequesting] = useState(false)
  const [previewLoading, setPreviewLoading] = useState(false)
  const t = useTranslations('modals.template-preview')
  const router = useRouter()

  /**
   * Handle "Use This Template" button click
   * Opens login modal if not authenticated, otherwise redirects to /new
   */
  const onGenerate = async () => {
    if (!user) {
      const redirectUrl = `/new?templateId=${templateId}`
      pushModal('LoginModal', { redirectUrl })
      return
    }

    if (requesting) return

    setRequesting(true)

    sendToBothAnalytics(
      MIXPANEL_CLIENT_EVENT_NAMES.CLICK_USE_TEMPLATE_BUTTON,
      {
        template_id: templateId,
        template_title: templateTitle,
      },
      user._id.toString(),
    )

    const params = new URLSearchParams({
      templateId: templateId,
      visibility: 'public',
    })

    const redirectUrl = `/new?${params.toString()}`
    popAllModals()
    router.push(redirectUrl)
  }

  /**
   * Handle "View in new tab" button click
   * Opens preview URL in new window
   */
  const onViewInNewTab = () => {
    setPreviewLoading(true)
    window.open(previewUrl, '_blank', 'noopener,noreferrer')
    sendToBothAnalytics(
      MIXPANEL_CLIENT_EVENT_NAMES.CLICK_PREVIEW_TEMPLATE_TAB,
      {
        template_id: templateId,
        template_title: templateTitle,
      },
      user?._id.toString() || '',
    )
    setTimeout(() => {
      setPreviewLoading(false)
    }, 1000)
  }

  return (
    <div className="flex items-center gap-2 w-full sm:w-auto">
      {/* View in new tab button */}
      <button
        type="button"
        onClick={onViewInNewTab}
        disabled={previewLoading}
        className="flex-1 sm:flex-initial sm:w-[170px] h-[38px] px-4 py-2 text-sm font-medium bg-white/20 text-white hover:bg-white/30 disabled:hover:bg-white/20 disabled:opacity-70 rounded-lg transition-colors flex items-center justify-center gap-2"
      >
        {previewLoading ? (
          <>
            <span>{t('loading')}</span>
            <Loader2Icon className="w-4 h-4 animate-spin" />
          </>
        ) : (
          <>
            <span>{t('view_in_new_tab')}</span>
            <ExternalLink className="w-4 h-4" />
          </>
        )}
      </button>

      {/* Use This Template button */}
      <button
        type="button"
        onClick={onGenerate}
        disabled={requesting}
        className={`flex-1 sm:flex-initial px-6 py-2 text-sm font-bold rounded-lg transition-colors ${
          !requesting
            ? 'bg-white text-black hover:bg-white/90'
            : 'bg-white/20 text-white/50 cursor-not-allowed'
        } ${requesting ? 'pointer-events-none' : ''}`}
      >
        {requesting ? (
          <Loader2Icon className="w-4 h-4 animate-spin mx-auto" />
        ) : (
          t('use_template')
        )}
      </button>
    </div>
  )
}
